import { Dialog } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import type { ComponentType } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';
import type { TemplateRef } from '@angular/core';
import { take } from 'rxjs';
import { generateRandomId } from '../../shared/others/utils';
import { PalantirDialogAnimatedContainerComponent } from './components/dialog-animated-container/dialog-animated-container.component';
import { PalantirDialogRef } from './dialog-ref.service';
import type { PalantirDialogOptions } from './dialog.type';
import { dialogLogger } from './logger.instance';

const logger = dialogLogger.extend('service');

@Injectable({
  providedIn: 'root',
})
export class PalantirDialogService {
  public constructor(
    private readonly dialog: Dialog,
    private readonly overlay: Overlay,
    private readonly injector: Injector,
  ) {}

  public openDialog(
    component: ComponentType<unknown> | TemplateRef<unknown>,
    options?: PalantirDialogOptions,
  ): PalantirDialogRef {
    const size = this.getSize(options?.size);

    const palantirDialogRef = new PalantirDialogRef(this.injector);

    const debugId = logger.enabled('debug') ? generateRandomId() : null;

    logger.debug('Dialog opened (DebugId: ' + debugId + ')', options, size);
    palantirDialogRef.setDebugId(debugId);

    if (options?.data && typeof options?.data !== 'object') {
      logger.warn(
        'Dialog data is not an object, it will be spread and may cause unexpected behavior',
        options?.data,
      );
    }

    const dialogRef = this.dialog.open(component, {
      panelClass: 'palantir-dialog-container',
      width: size.width,
      maxWidth: size.maxWidth,
      maxHeight: 'calc(100% - 128px)',
      height: options?.fullHeight ? '100%' : undefined,
      autoFocus: options?.autoFocus ?? false,
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      data: {
        ...options?.data,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        canClose: options?.canClose,
        confirmation: options?.confirmation,
      },
      disableClose: true,
      providers: (dialogRef) => {
        palantirDialogRef.dialogRef = dialogRef;

        return [
          {
            provide: PalantirDialogRef,
            useValue: palantirDialogRef,
          },
        ];
      },
      container: PalantirDialogAnimatedContainerComponent,
    });

    palantirDialogRef.dialogRef = dialogRef;

    if (logger.enabled('debug')) {
      dialogRef.closed.pipe(take(1)).subscribe(() => {
        logger.debug('Dialog closed (DebugId: ' + debugId + ')');
      });
    }

    return palantirDialogRef;
  }

  private getSize(size?: PalantirDialogOptions['size']): { maxWidth: string; width: string } {
    const widthBySize = {
      '2x-large': '1200px',
      'x-large': '1000px',
      large: '800px',
      medium: '600px',
      small: '400px',
    };

    return {
      width: widthBySize[size ?? 'small'],
      maxWidth: widthBySize[size ?? 'small'],
    };
  }
}
